import { useState } from 'react';
import { useMapStore } from '@/stores/map-store';
import { useHealthStore } from '@/stores/health-store';
import { useSidebarStore } from '@/stores/sidebar-store';
import { usePluginStore } from '@/stores/plugin-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import type { ConfigSource } from '@vtak/shared';
import { CORE_NAV_ITEMS } from '@/constants/navigation';
import type { NavItem } from '@/constants/navigation';
import { ICON_RAIL_WIDTH } from '@/constants/layout';
import { Button } from '@/components/ui/button';
import { RailTooltip } from './rail-tooltip';
import { cn } from '@/lib/utils';
import { Blocks, Settings as SettingsIcon } from 'lucide-react';
import { CLIENT_CONFIG } from '@/config';
import vtakLogo from '@/assets/vtak-logo.png';
import vertexMark from '@/assets/vertex-mark.svg';

/** Fixed left icon rail with core nav + single plugins entry point */
export function IconRail(): React.JSX.Element {
  const activePanel = useSidebarStore((s) => s.activePanel);
  const togglePanel = useSidebarStore((s) => s.togglePanel);
  const isConnected = useHealthStore((s) => s.isConnected);
  const activePlugins = usePluginStore((s) => s.activePlugins);
  const role = useWorkspaceStore((s) => s.role);
  const platformAdmin = useWorkspaceStore((s) => s.platformAdmin);
  const corePanelFlags = useWorkspaceStore((s) => s.workspace?.corePanelFlags);
  const configSource = useWorkspaceStore((s) => s.configSource);
  const hasPlugins = activePlugins.length > 0;
  const navItems = visibleNavItems(role === 'admin' || platformAdmin, corePanelFlags);

  return (
    <nav
      className="absolute top-0 left-0 z-[1001] flex h-full flex-col border-r border-echo-border bg-[#1a1a1a]"
      style={{ width: ICON_RAIL_WIDTH }}
    >
      <Branding />
      <CoreNavSection items={navItems} activePanel={activePanel} onToggle={togglePanel} />
      {hasPlugins && (
        <PluginsButton
          isActive={activePanel === 'plugins'}
          pluginCount={activePlugins.length}
          onClick={() => togglePanel('plugins')}
        />
      )}
      <div className="flex-1" />
      <RailFooter
        isConnected={isConnected}
        configSource={configSource}
        settingsActive={activePanel === 'settings'}
        onToggleSettings={() => togglePanel('settings')}
      />
    </nav>
  );
}

/** Filter core nav items by admin role and profile core-panel flags */
function visibleNavItems(
  isAdmin: boolean,
  corePanelFlags: Record<string, boolean> | undefined,
): NavItem[] {
  return CORE_NAV_ITEMS.filter((item) => {
    if (item.hidden) return false;
    if (item.adminOnly && !isAdmin) return false;
    if (corePanelFlags?.[item.id] === false) return false;
    return true;
  });
}

/** VTAK branding at the top of the rail */
function Branding(): React.JSX.Element {
  return (
    <div className="flex items-center justify-center py-4">
      <img
        src={vtakLogo}
        alt="VTAK"
        className="w-10 h-auto drop-shadow-[0_0_6px_rgba(255,255,255,0.15)]"
      />
    </div>
  );
}

/** Core navigation icons, filtered to those visible for the active workspace */
function CoreNavSection({
  items,
  activePanel,
  onToggle,
}: {
  items: NavItem[];
  activePanel: string | null;
  onToggle: (id: string) => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-1 py-2">
      {items.map((item) => (
        <NavButton
          key={item.id}
          item={item}
          isActive={activePanel === item.id}
          onClick={() => onToggle(item.id)}
        />
      ))}
    </div>
  );
}

/** Single "Plugins" button with active plugin count badge */
function PluginsButton({
  isActive,
  pluginCount,
  onClick,
}: {
  isActive: boolean;
  pluginCount: number;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center border-t border-echo-border pt-2">
      <RailTooltip label={`Plugins (${pluginCount} active)`}>
        <button
          onClick={onClick}
          aria-label="Plugins"
          className={cn(
            'relative flex h-10 w-10 items-center justify-center rounded-md transition-colors',
            isActive
              ? 'bg-echo-yellow/15 text-echo-yellow'
              : 'text-muted-foreground hover:bg-white/[0.06] hover:text-foreground',
          )}
        >
          {isActive && (
            <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-echo-yellow" />
          )}
          <Blocks className="h-5 w-5" />
          <PluginCountBadge count={pluginCount} />
        </button>
      </RailTooltip>
    </div>
  );
}

/** Small badge showing active plugin count */
function PluginCountBadge({ count }: { count: number }): React.JSX.Element {
  return (
    <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-ai px-1 text-[8px] font-bold text-white">
      {count}
    </span>
  );
}

/** Single nav icon button in the rail */
function NavButton({
  item,
  isActive,
  onClick,
}: {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const Icon = item.icon;

  return (
    <RailTooltip label={item.label}>
      <button
        onClick={onClick}
        aria-label={item.label}
        className={cn(
          'relative flex h-10 w-10 items-center justify-center rounded-md transition-colors',
          isActive
            ? 'bg-echo-yellow/15 text-echo-yellow'
            : 'text-muted-foreground hover:bg-white/[0.06] hover:text-foreground',
        )}
      >
        {isActive && (
          <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-echo-yellow" />
        )}
        {item.iconAsset ? (
          <img
            src={item.iconAsset}
            alt={item.label}
            className={cn('h-5 w-5', !isActive && 'opacity-60')}
          />
        ) : (
          <Icon className="h-5 w-5" />
        )}
      </button>
    </RailTooltip>
  );
}

/** Bottom section: settings, Vertex link, 2D/3D toggle, config source + connection status */
function RailFooter({
  isConnected,
  configSource,
  settingsActive,
  onToggleSettings,
}: {
  isConnected: boolean;
  configSource: ConfigSource;
  settingsActive: boolean;
  onToggleSettings: () => void;
}): React.JSX.Element {
  const viewMode = useMapStore((s) => s.viewMode);
  const setViewMode = useMapStore((s) => s.setViewMode);
  const nextMode = viewMode === '2d' ? '3d' : '2d';

  return (
    <div className="flex flex-col items-center gap-2 border-t border-echo-border py-3">
      <RailTooltip label="Settings">
        <button
          onClick={onToggleSettings}
          aria-label="Settings"
          className={cn(
            'relative flex h-10 w-10 items-center justify-center rounded-md transition-colors',
            settingsActive
              ? 'bg-echo-yellow/15 text-echo-yellow'
              : 'text-muted-foreground hover:bg-white/[0.06] hover:text-foreground',
          )}
        >
          {settingsActive && (
            <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r bg-echo-yellow" />
          )}
          <SettingsIcon className="h-5 w-5" />
        </button>
      </RailTooltip>
      <VertexButton />
      <RailTooltip label={`Switch to ${nextMode.toUpperCase()} map`}>
        <Button
          size="sm"
          variant="outline"
          className="h-7 w-10 bg-transparent border-echo-border text-[10px] font-bold text-muted-foreground hover:bg-white/[0.06]"
          onClick={() => setViewMode(nextMode)}
        >
          {nextMode.toUpperCase()}
        </Button>
      </RailTooltip>
      <ConfigSourceDot configSource={configSource} />
      <RailTooltip label={isConnected ? 'TAK Connected' : 'TAK Disconnected'}>
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              isConnected ? 'bg-green-500' : 'bg-red-500',
            )}
          />
        </div>
      </RailTooltip>
    </div>
  );
}

/** Config-source indicator — dot plus title so status is never color-only */
function ConfigSourceDot({ configSource }: { configSource: ConfigSource }): React.JSX.Element {
  const color =
    configSource === 'live' ? 'bg-green-500' : configSource === 'cached' ? 'bg-amber-500' : 'bg-red-500';
  const label =
    configSource === 'live'
      ? 'Config: live'
      : configSource === 'cached'
        ? 'Config: cached'
        : 'Config: degraded';

  return (
    <RailTooltip label={label}>
      <div className="flex items-center gap-1.5">
        <span className={cn('h-2 w-2 rounded-full', color)} />
      </div>
    </RailTooltip>
  );
}

/** Return to Vertex Hub button with confirmation dialog */
function VertexButton(): React.JSX.Element | null {
  const vertexUrl = CLIENT_CONFIG.vertexUrl;
  const [showConfirm, setShowConfirm] = useState(false);

  if (!vertexUrl) return null;

  return (
    <>
      <RailTooltip label="Return to Vertex Hub">
        <button
          onClick={() => setShowConfirm(true)}
          aria-label="Return to Vertex Hub"
          className="group flex h-10 w-10 items-center justify-center rounded-lg border border-echo-border/50 bg-gradient-to-br from-white/[0.04] to-white/[0.01] transition-all hover:border-[#00C9FF]/30 hover:from-white/[0.08] hover:to-white/[0.03] hover:shadow-[0_0_8px_rgba(0,201,255,0.15)]"
        >
          <img
            src={vertexMark}
            alt="Vertex"
            className="h-5 w-5 opacity-60 transition-opacity group-hover:opacity-100"
          />
        </button>
      </RailTooltip>

      {showConfirm && (
        <VertexConfirmDialog
          vertexUrl={vertexUrl}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
}

/** Confirmation dialog before navigating to Vertex */
function VertexConfirmDialog({
  vertexUrl,
  onCancel,
}: {
  vertexUrl: string;
  onCancel: () => void;
}): React.JSX.Element {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-xl border border-echo-border bg-[#1e1e1e] p-6 shadow-2xl">
        <div className="mb-4 flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#F4C43F]/20 to-[#00C9FF]/20">
            <img src={vertexMark} alt="Vertex" className="h-7 w-7" />
          </div>
        </div>

        <h3 className="mb-2 text-center text-sm font-semibold text-foreground">
          Return to Vertex
        </h3>
        <p className="mb-6 text-center text-xs text-muted-foreground leading-relaxed">
          You are about to leave VTAK and return to the Vertex Hub.
          Any unsaved work will be lost.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 rounded-lg border border-echo-border px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-white/[0.08]"
          >
            Stay Here
          </button>
          <a
            href={vertexUrl}
            className="flex-1 rounded-lg bg-[#00A3E0] px-4 py-2.5 text-center text-sm font-semibold text-white shadow-lg shadow-[#00A3E0]/25 transition-all hover:bg-[#008CC4] hover:shadow-[#00A3E0]/40"
          >
            Continue to Vertex
          </a>
        </div>
      </div>
    </div>
  );
}
